const BUNDLE_BUTTON_LABELS = {
  idle: 'הוסף את כל החבילה לעגלה',
  loading: 'מוסיף את החבילה...',
  success: 'נוסף לעגלה',
  error: 'נסה שוב'
};

const BUNDLE_BUY_NOW_LABELS = {
  idle: 'קנה עכשיו',
  loading: 'מעביר לקופה...',
  success: 'מעביר לקופה...',
  error: 'נסה שוב'
};

class BundleProductsView {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-bundle-grid]');
    this.cards = [...root.querySelectorAll('[data-bundle-item]')];
    this.button = root.querySelector('[data-bundle-add]');
    this.buttonText = root.querySelector('[data-bundle-button-text]');
    this.buyNowButton = root.querySelector('[data-bundle-buy-now]');
    this.buyNowButtonText = root.querySelector('[data-bundle-buy-now-text]');
    this.message = root.querySelector('[data-bundle-message]');
    this.spinner = this.button?.querySelector('.loading__spinner') || null;
    this.buyNowSpinner = this.buyNowButton?.querySelector('.loading__spinner') || null;
    this.totalPrice = root.querySelector('[data-bundle-total-cents]');
    this.savePrice = root.querySelector('[data-bundle-save-cents]');
    this.saveRow = root.querySelector('[data-bundle-save-row]');
    this.count = root.querySelector('[data-bundle-count]');
    this.currency = root.dataset.currency || '₪';
    this.currencyCode = root.dataset.currencyCode || 'ILS';
    this.locale = root.dataset.locale || document.documentElement.lang || 'he-IL';
    this.mainVariantId = Number(root.dataset.mainVariantId || 0);
    this.successTimeout = null;
  }

  getAvailableCards() {
    return this.cards.filter((card) => card.dataset.unavailable !== 'true');
  }

  getItems() {
    return this.getAvailableCards()
      .map((card) => ({
        id: Number(card.dataset.variantId),
        quantity: Number(card.dataset.quantity || 1),
        productTitle: card.dataset.productTitle || '',
        variantTitle: card.dataset.variantTitle || ''
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);
  }

  getBundleLineItem() {
    const items = this.getItems();
    if (!Number.isFinite(this.mainVariantId) || this.mainVariantId <= 0) {
      return null;
    }

    const totals = this.getTotals();
    const groupedItems = new Map();

    items.forEach((item) => {
      const groupKey = item.productTitle || 'Bundle item';
      if (!groupedItems.has(groupKey)) {
        groupedItems.set(groupKey, new Map());
      }

      const groupLines = groupedItems.get(groupKey);
      const normalizedVariant = (item.variantTitle || '').trim();
      const variantKey = normalizedVariant && normalizedVariant !== 'Default Title' ? normalizedVariant : '__default__';
      const previousQuantity = groupLines.get(variantKey) || 0;
      groupLines.set(variantKey, previousQuantity + item.quantity);
    });

    const properties = {
      _bundle: 'true',
      _bundle_total: String(totals.totalCents),
      _bundle_count: String(totals.count)
    };

    [...groupedItems.entries()].forEach(([productTitle, lines], index) => {
      const serializedLines = [...lines.entries()]
        .map(([variantLabel, quantity]) =>
          variantLabel === '__default__' ? `× ${quantity}` : `${variantLabel} × ${quantity}`
        )
        .join('||');
      properties[`_bundle_group_${index + 1}`] = `${productTitle}::${serializedLines}`;
    });

    return {
      id: this.mainVariantId,
      quantity: 1,
      properties
    };
  }

  getTotals() {
    return this.getAvailableCards().reduce(
      (totals, card) => {
        const quantity = Number(card.dataset.quantity || 1);
        const priceCents = Number(card.dataset.priceCents || 0);
        const compareAtCents = Number(card.dataset.compareAtCents || priceCents);

        totals.count += quantity;
        totals.totalCents += priceCents * quantity;
        totals.compareAtCents += compareAtCents * quantity;
        return totals;
      },
      { count: 0, totalCents: 0, compareAtCents: 0 }
    );
  }

  formatMoney(cents) {
    try {
      const amount = new Intl.NumberFormat(this.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);

      return `${this.currency}${amount}`;
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${this.currency}`;
    }
  }

  syncSummary() {
    const totals = this.getTotals();
    const saveCents = Math.max(0, totals.compareAtCents - totals.totalCents);

    if (this.count) {
      this.count.textContent = String(totals.count);
    }

    if (this.totalPrice) {
      this.totalPrice.dataset.bundleTotalCents = String(totals.totalCents);
      this.totalPrice.textContent = this.formatMoney(totals.totalCents);
    }

    if (this.savePrice) {
      this.savePrice.dataset.bundleSaveCents = String(saveCents);
      this.savePrice.textContent = saveCents > 0 ? this.formatMoney(saveCents) : '';
    }

    if (this.saveRow) {
      this.saveRow.classList.toggle('is-hidden', saveCents <= 0);
    }
  }

  setButtonState(state, type = 'cart') {
    const button = type === 'checkout' ? this.buyNowButton : this.button;
    const buttonText = type === 'checkout' ? this.buyNowButtonText : this.buttonText;
    const labels = type === 'checkout' ? BUNDLE_BUY_NOW_LABELS : BUNDLE_BUTTON_LABELS;

    if (!button) return;

    button.dataset.state = state;

    if (buttonText) {
      buttonText.textContent = labels[state] || labels.idle;
    }
  }

  setLoading(isLoading, type = 'cart') {
    const activeButton = type === 'checkout' ? this.buyNowButton : this.button;
    const activeSpinner = type === 'checkout' ? this.buyNowSpinner : this.spinner;
    const passiveButton = type === 'checkout' ? this.button : this.buyNowButton;

    if (!activeButton) return;

    activeButton.classList.toggle('loading', isLoading);
    activeButton.toggleAttribute('aria-disabled', isLoading);
    activeButton.disabled = isLoading || activeButton.hasAttribute('data-force-disabled');
    this.setButtonState(isLoading ? 'loading' : 'idle', type);

    if (passiveButton) {
      passiveButton.disabled = isLoading || passiveButton.hasAttribute('data-force-disabled');
      passiveButton.toggleAttribute('aria-disabled', isLoading);
    }

    if (activeSpinner) {
      activeSpinner.classList.toggle('hidden', !isLoading);
    }
  }

  setDisabled(disabled) {
    [this.button, this.buyNowButton].forEach((button) => {
      if (!button) return;

      if (disabled) {
        button.setAttribute('data-force-disabled', 'true');
        button.disabled = true;
      } else {
        button.removeAttribute('data-force-disabled');
        button.disabled = false;
      }
    });
  }

  setMessage(message, tone = '') {
    if (!this.message) return;

    this.message.hidden = !message;
    this.message.textContent = message || '';
    this.message.dataset.tone = tone;
  }

  clearMessage() {
    this.setMessage('');
  }

  animateAddedCards() {
    this.cards.forEach((card) => {
      card.classList.remove('is-added');
      void card.offsetWidth;
      card.classList.add('is-added');

      window.setTimeout(() => {
        card.classList.remove('is-added');
      }, 1200);
    });
  }

  showSuccessState(type = 'cart') {
    const button = type === 'checkout' ? this.buyNowButton : this.button;
    if (!button) return;

    window.clearTimeout(this.successTimeout);
    this.setButtonState('success', type);
    button.classList.add('is-success');
    button.removeAttribute('aria-disabled');
    button.disabled = button.hasAttribute('data-force-disabled');

    this.successTimeout = window.setTimeout(() => {
      button.classList.remove('is-success');
      this.setButtonState('idle', type);
    }, 1800);
  }

  attachObservers() {
    if (!this.grid || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(() => {
      this.cards = [...this.root.querySelectorAll('[data-bundle-item]')];
      this.syncSummary();
    });

    observer.observe(this.grid, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-price-cents', 'data-compare-at-cents', 'data-quantity', 'data-unavailable']
    });
  }
}

class BundleProductsCart {
  constructor(root) {
    this.root = root;
    this.cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  get addUrl() {
    return this.root.dataset.cartAddUrl || '/cart/add.js';
  }

  get cartUrl() {
    return this.root.dataset.cartUrl || window.routes?.cart_url || '/cart';
  }

  get checkoutUrl() {
    return '/checkout';
  }

  getSectionsToRender() {
    if (!this.cartUI || typeof this.cartUI.getSectionsToRender !== 'function') {
      return [];
    }

    return this.cartUI.getSectionsToRender().map((section) => section.id);
  }

  async addItem(item) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    const payload = { items: [item] };
    const sections = this.getSectionsToRender();

    if (sections.length > 0) {
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
      if (typeof this.cartUI?.setActiveElement === 'function') {
        this.cartUI.setActiveElement(document.activeElement);
      }
    }

    config.body = JSON.stringify(payload);

    const response = await fetch(this.addUrl, config);
    const parsedResponse = await response.json();

    if (!response.ok || parsedResponse.status) {
      throw new Error(parsedResponse.description || parsedResponse.message || 'Could not add bundle to cart.');
    }

    if (sections.length > 0 && parsedResponse.sections && this.cartUI) {
      this.cartUI.renderContents(parsedResponse);
    }
    return parsedResponse;
  }
}

class BundleProductsController {
  constructor(root) {
    this.root = root;
    this.view = new BundleProductsView(root);
    this.cart = new BundleProductsCart(root);

    if (!this.view.button && !this.view.buyNowButton) return;

    this.view.syncSummary();
    this.view.attachObservers();
    this.bind();
  }

  bind() {
    this.view.button?.addEventListener('click', () => this.handleAction('cart'));
    this.view.buyNowButton?.addEventListener('click', () => this.handleAction('checkout'));
  }

  async handleAction(mode) {
    const bundleItem = this.view.getBundleLineItem();

    if (!bundleItem) {
      this.view.setDisabled(true);
      this.view.setMessage('הווריאנט הראשי של החבילה לא זמין כרגע.', 'error');
      return;
    }

    if (this.view.getItems().length === 0) {
      this.view.setDisabled(true);
      this.view.setMessage('לא נמצאו מוצרים זמינים בחבילה.', 'error');
      return;
    }

    this.view.clearMessage();
    this.view.setLoading(true, mode);

    try {
      await this.cart.addItem(bundleItem);
      this.view.setMessage(mode === 'checkout' ? 'מעביר לקופה...' : 'החבילה נוספה לעגלה.', 'success');
      this.view.animateAddedCards();
      this.view.showSuccessState(mode);
      if (mode === 'checkout') {
        window.location = this.cart.checkoutUrl;
        return;
      }
      if (!this.cart.cartUI) {
        window.location = this.cart.cartUrl;
        return;
      }
    } catch (error) {
      this.view.setButtonState('error', mode);
      this.view.setMessage(error.message, 'error');
    } finally {
      [this.view.spinner, this.view.buyNowSpinner].forEach((spinner) => {
        if (spinner) spinner.classList.add('hidden');
      });

      [this.view.button, this.view.buyNowButton].forEach((button) => {
        button?.classList.remove('loading');
        button?.removeAttribute('aria-disabled');
        if (button && !button.hasAttribute('data-force-disabled')) {
          button.disabled = false;
        }
      });
      if (mode !== 'checkout') {
        this.view.setButtonState('idle', 'checkout');
      }
    }
  }
}

function initBundleProducts(container = document) {
  const roots = container.querySelectorAll('[data-bundle-root]');

  roots.forEach((root) => {
    if (root.dataset.bundleInitialized === 'true') return;
    root.dataset.bundleInitialized = 'true';
    new BundleProductsController(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initBundleProducts();
});

document.addEventListener('shopify:section:load', (event) => {
  initBundleProducts(event.target);
});
